import { useState, useEffect } from 'react';
import { Search, Save, ChevronDown, ChevronRight, UserCheck } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { api } from '../../utils/api';

export default function TeacherMapping() {
  const { staff, classes, subjects } = useApp();
  const [search, setSearch]   = useState('');
  const [expanded, setExpanded] = useState(null);
  // staffId → { classTeacherOf, classSubjectPairs: [{classId, subjectId}] }
  const [mappings, setMappings] = useState({});
  const [saving, setSaving]   = useState(null);
  const [saved,  setSaved]    = useState(null);

  useEffect(() => {
    api.list('staff-full').then(list => {
      if (!Array.isArray(list)) return;
      const init = {};
      list.forEach(s => {
        const classTeacherOf = classes.find(c => c.classTeacherId === s.id)?.id || '';
        init[s.id] = {
          classTeacherOf,
          classSubjectPairs: Array.isArray(s.classSubjectPairs) ? s.classSubjectPairs : [],
        };
      });
      setMappings(init);
    }).catch(() => {});
  }, [classes]); // eslint-disable-line react-hooks/exhaustive-deps

  const teachers = staff
    .filter(s => s.status === 'active')
    .filter(s => !search ||
      `${s.firstName} ${s.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
      s.designation?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));

  const get = (id) => mappings[id] || { classTeacherOf: '', classSubjectPairs: [] };

  const setClassTeacher = (staffId, val) =>
    setMappings(prev => ({ ...prev, [staffId]: { ...get(staffId), classTeacherOf: val } }));

  const togglePair = (staffId, classId, subjectId) => {
    const cur = get(staffId).classSubjectPairs;
    const exists = cur.some(p => p.classId === classId && p.subjectId === subjectId);
    const next = exists
      ? cur.filter(p => !(p.classId === classId && p.subjectId === subjectId))
      : [...cur, { classId, subjectId }];
    setMappings(prev => ({ ...prev, [staffId]: { ...get(staffId), classSubjectPairs: next } }));
  };

  const isPaired = (staffId, classId, subjectId) =>
    get(staffId).classSubjectPairs.some(p => p.classId === classId && p.subjectId === subjectId);

  const save = async (staffId) => {
    setSaving(staffId); setSaved(null);
    try {
      await api.update('teacher-mapping', staffId, get(staffId));
      setSaved(staffId);
      setTimeout(() => setSaved(null), 2000);
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally { setSaving(null); }
  };

  // Subjects belonging to a class (via classIds on subject)
  const subjectsForClass = (classId) =>
    subjects.filter(s => (s.classIds || []).includes(classId));

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Teacher Assignment</h1>
        <p className="text-gray-500 text-sm">
          Assign each teacher their class teacher role and which subjects they teach in each class
        </p>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" placeholder="Search teachers…" value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400" />
      </div>

      <div className="space-y-2">
        {teachers.map(t => {
          const m      = get(t.id);
          const isOpen = expanded === t.id;
          const ctClass = classes.find(c => c.id === m.classTeacherOf);
          const pairCount = m.classSubjectPairs.length;

          return (
            <div key={t.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Header */}
              <button onClick={() => setExpanded(isOpen ? null : t.id)}
                className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-gray-50 transition-colors text-left">
                <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm flex-shrink-0">
                  {t.firstName?.[0]}{t.lastName?.[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-900">{t.firstName} {t.lastName}</p>
                  <p className="text-xs text-gray-500">{t.designation}{t.department ? ` · ${t.department}` : ''}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {m.classTeacherOf && (
                    <span className="bg-indigo-100 text-indigo-700 text-xs font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                      <UserCheck size={11} /> Class Teacher · {ctClass ? `${ctClass.name}-${ctClass.section}` : m.classTeacherOf}
                    </span>
                  )}
                  {pairCount > 0 && (
                    <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {pairCount} assignment{pairCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {isOpen
                  ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
                  : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />}
              </button>

              {/* Expanded panel */}
              {isOpen && (
                <div className="border-t border-gray-100 px-5 py-4 space-y-5 bg-gray-50/40">

                  {/* Class Teacher Of */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-1.5">
                      Class Teacher Of
                    </label>
                    <select value={m.classTeacherOf} onChange={e => setClassTeacher(t.id, e.target.value)}
                      className="w-full sm:w-72 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white">
                      <option value="">— None (Subject Teacher) —</option>
                      {classes.map(c => (
                        <option key={c.id} value={c.id}>{c.name} – Section {c.section}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">
                      Class teachers can assign homework for all subjects in their class.
                    </p>
                  </div>

                  {/* Class × Subject grid */}
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
                      Subject Assignments — select which subject this teacher teaches in each class
                    </label>
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      {classes.map((cls, ci) => {
                        const subs = subjectsForClass(cls.id);
                        if (subs.length === 0) return null;
                        return (
                          <div key={cls.id}
                            className={`flex items-start gap-4 px-4 py-3 ${ci % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${ci !== 0 ? 'border-t border-gray-100' : ''}`}>
                            {/* Class label */}
                            <div className="w-28 flex-shrink-0 pt-0.5">
                              <span className="inline-block bg-indigo-50 text-indigo-700 text-xs font-bold px-2.5 py-1 rounded-lg">
                                {cls.name}-{cls.section}
                              </span>
                            </div>
                            {/* Subject chips */}
                            <div className="flex flex-wrap gap-1.5">
                              {subs.map(sub => {
                                const active = isPaired(t.id, cls.id, sub.id);
                                return (
                                  <button key={sub.id}
                                    onClick={() => togglePair(t.id, cls.id, sub.id)}
                                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                                      active
                                        ? 'bg-green-600 text-white border-green-600 shadow-sm'
                                        : 'bg-white text-gray-500 border-gray-300 hover:border-green-400 hover:text-green-600'
                                    }`}>
                                    {active ? '✓ ' : ''}{sub.name}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-400 mt-1.5">
                      Green = assigned. Click to toggle. A teacher can teach different subjects in different classes.
                    </p>
                  </div>

                  {/* Save */}
                  <div className="flex items-center gap-3 pt-1">
                    <button onClick={() => save(t.id)} disabled={saving === t.id}
                      className="flex items-center gap-2 px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg disabled:opacity-60 transition-colors">
                      <Save size={14} />
                      {saving === t.id ? 'Saving…' : 'Save Assignment'}
                    </button>
                    {saved === t.id && (
                      <span className="text-green-600 text-sm font-medium">✓ Saved</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {teachers.length === 0 && (
          <div className="bg-white rounded-xl p-10 text-center text-gray-400">No teachers found</div>
        )}
      </div>
    </div>
  );
}
